import React, {
    ForwardedRef,
    InputHTMLAttributes,
    MutableRefObject,
    forwardRef,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useIMask } from 'react-imask'
import { twMerge as twm } from 'tailwind-merge'

import { Icon, IconNameAndCategoryString } from '../../Icon'
import { Spinner } from '../../Spinner'
import Button from '../../app/design/Button'
import Label from '../../app/design/Label'

export type TextFieldChip = {
    id: string | number
    title: string
}

export type Props<ChipType extends TextFieldChip = TextFieldChip> = Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'type' | 'size'
> & {
    type?: 'text' | 'password' | 'number' | 'email'
    hasError?: boolean
    mask?: string | Parameters<typeof useIMask>
    isFullWidth?: boolean
    size?: 'small' | 'medium'
    icon?: IconNameAndCategoryString | React.ReactNode
    chips?: ChipType[]
    onRemoveChipWithID?: (chipID: TextFieldChip['id']) => void
    onClear?: () => void
    clearButtonOffset?: number
    isLoading?: boolean
    secondaryButton?: {
        label: string
        onClick: () => void
    }
}

const TextField = forwardRef(
    <ChipType extends TextFieldChip = TextFieldChip>(
        {
            type = 'text',
            value,
            placeholder,
            hasError = false,
            size = 'small',
            icon,
            disabled = false,
            isFullWidth = false,
            className,
            onFocus,
            onBlur,
            onKeyDown,
            chips,
            onRemoveChipWithID,
            onClear,
            clearButtonOffset,
            style,
            mask,
            isLoading = false,
            secondaryButton,
            ...props
        }: Props<ChipType>,
        forwardedRef: ForwardedRef<HTMLInputElement>
    ) => {
        // Some bug in the react-imask library (or our usage of it) causes the mask to not
        // work when it's already been set on mount. But if we set it a tick later it works
        // fine. This state and effect handles that one tick delay automatically.
        const [effectiveMask, setEffectiveMask] = useState<Props['mask']>(undefined)
        useEffect(() => setEffectiveMask(mask), [mask])

        const { ref: imaskRef } = useIMask(
            effectiveMask === undefined || typeof effectiveMask === 'string'
                ? { mask: effectiveMask }
                : effectiveMask[0],
            effectiveMask === undefined || typeof effectiveMask === 'string'
                ? undefined
                : effectiveMask[1]
        )

        const inputRef = useRef(null) as MutableRefObject<HTMLInputElement | null>
        const chipsContainerRef = useRef<HTMLDivElement>(null)
        const rightSideButtonsContainerRef = useRef<HTMLDivElement>(null)
        const clearButtonRef = useRef<HTMLButtonElement>(null)

        const [isFocused, setIsFocused] = useState(false)
        const [isFieldGroupFocused, setIsFieldGroupFocused] = useState(false)

        // This additional padding must be set to avoid overlap with any chips that may be visible.
        const [additionalFieldPadding, setAdditionalFieldPadding] = useState({ top: 0, left: 0 })
        // This padding is calcualted from the width of the container on the right side containing
        // the loading indicator, clear button, and secondary button.
        const [rightFieldPadding, setRightFieldPadding] = useState(0)

        const textFieldID = useMemo(() => Math.random().toString(), [])

        const handleFocus = useCallback(
            (event: React.FocusEvent<HTMLInputElement, Element>) => {
                setIsFocused(true)
                setIsFieldGroupFocused(true)
                onFocus?.(event)
            },
            [onFocus]
        )
        const handleBlur = useCallback(
            (event: React.FocusEvent<HTMLInputElement, Element>) => {
                setIsFocused(false)

                // Use timeout to allow checking the next focused element
                setTimeout(() => {
                    if (document.activeElement !== clearButtonRef.current) {
                        setIsFieldGroupFocused(false)
                    }
                }, 0)

                onBlur?.(event)
            },
            [onBlur]
        )
        const handleClearButtonBlur = useCallback(() => {
            // Use timeout to allow checking the next focused element
            setTimeout(() => {
                if (document.activeElement !== inputRef.current) {
                    setIsFieldGroupFocused(false)
                }
            }, 0)
        }, [])
        const handleKeyDown = useCallback(
            (event: React.KeyboardEvent<HTMLInputElement>) => {
                if (
                    event.key === 'Backspace' &&
                    value === '' &&
                    chips !== undefined &&
                    onRemoveChipWithID !== undefined &&
                    chips.length > 0
                ) {
                    event.preventDefault()
                    event.stopPropagation()
                    onRemoveChipWithID(chips.at(-1)!.id)
                    return
                }
                onKeyDown?.(event)
            },
            [onKeyDown, value, chips, onRemoveChipWithID]
        )

        const handleClear = useCallback(() => {
            if (inputRef.current) inputRef.current.focus()
            onClear?.()
        }, [onClear])

        const isClearButtonVisible = onClear !== undefined && !!value && isFieldGroupFocused

        const isSecondaryButtonVisible = secondaryButton !== undefined && value === ''

        useLayoutEffect(() => {
            const rightSideButtonsContainer = rightSideButtonsContainerRef.current
            if (rightSideButtonsContainer === null) {
                setRightFieldPadding(16)
                return
            }

            setRightFieldPadding(rightSideButtonsContainer.offsetWidth)
        }, [isClearButtonVisible, isLoading, isSecondaryButtonVisible])

        // This layout effect calculates the amount of padding necessary for
        // the text field to avoid the chips.
        useLayoutEffect(() => {
            if (chips === undefined) {
                setAdditionalFieldPadding({ top: 0, left: 0 })
                return
            }

            const chipsContainer = chipsContainerRef.current
            const textField = document.querySelector(`[data-text-field-id='${textFieldID}']`)
            if (chipsContainer === null || textField === null) {
                return
            }

            const lastChipElement = [...chipsContainer.children].at(-1)
            if (lastChipElement === undefined) {
                setAdditionalFieldPadding({ top: 0, left: 0 })
                return
            }

            const lastChipBoundingRect = lastChipElement.getBoundingClientRect()
            const textFieldBoundingRect = textField.getBoundingClientRect()

            const needsNewLine = textFieldBoundingRect.right - lastChipBoundingRect.right < 100

            setAdditionalFieldPadding({
                top:
                    lastChipBoundingRect.top -
                    textFieldBoundingRect.top -
                    (size === 'small' ? 7 : 11) +
                    (needsNewLine ? lastChipBoundingRect.height : 0),
                left: needsNewLine
                    ? 0
                    : lastChipBoundingRect.right - textFieldBoundingRect.left - 6,
            })
        }, [chips, size, onRemoveChipWithID, textFieldID])

        return (
            <div className={twm(`relative grid`, isFullWidth && 'w-full')}>
                <input
                    ref={(e) => {
                        if (forwardedRef && typeof forwardedRef === 'object') {
                            forwardedRef.current = e
                        }
                        if (effectiveMask && imaskRef) {
                            imaskRef.current = e
                        }
                        if (inputRef) {
                            inputRef.current = e
                        }
                    }}
                    {...props}
                    value={value}
                    type={type}
                    placeholder={chips !== undefined && chips.length > 0 ? undefined : placeholder}
                    className={twm(
                        'col-start-1 row-start-1 cursor-pointer rounded-lg border bg-white outline-0',
                        hasError && !isFocused ? 'outline-danger-400' : 'outline-primary-700',
                        size === 'small' ? 'text-paragraph-sm' : 'text-paragraph-md',
                        disabled
                            ? 'border-gray-400'
                            : hasError
                              ? 'border-danger-400'
                              : 'border-gray-500',
                        isFocused && !disabled
                            ? 'border-primary-700 outline-2'
                            : !disabled
                              ? 'hover:outline-1'
                              : null,
                        !isFocused &&
                            !disabled &&
                            (hasError ? 'hover:border-danger-400' : 'hover:border-primary-700'),
                        disabled && 'cursor-not-allowed',
                        isFullWidth ? 'w-full' : undefined,
                        className
                    )}
                    style={{
                        paddingTop: (size === 'small' ? 7 : 11) + additionalFieldPadding.top,
                        paddingBottom: size === 'small' ? 7 : 11,
                        paddingLeft:
                            (Boolean(icon) ? (size === 'small' ? 40 : 52) : 16) +
                            additionalFieldPadding.left,
                        paddingRight: rightFieldPadding,
                        ...style,
                    }}
                    disabled={disabled}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    data-text-field-id={textFieldID}
                    data-1p-ignore
                />

                <div
                    className={twm(
                        'pointer-events-none relative col-start-1 row-start-1 w-full',
                        size === 'small' ? 'pt-[7px]' : 'py-2',
                        Boolean(icon) ? (size === 'small' ? 'pl-[44px]' : 'pl-[48px]') : 'pl-4',
                        isClearButtonVisible ? 'pr-10' : 'pr-6'
                    )}
                    style={{ paddingRight: rightFieldPadding !== 0 ? rightFieldPadding : 24 }}
                >
                    <div
                        ref={chipsContainerRef}
                        className="flex flex-wrap items-center justify-start gap-1"
                    >
                        {chips !== undefined
                            ? chips.map((chip) => (
                                  <TextFieldChip
                                      key={chip.id}
                                      title={chip.title}
                                      onRemove={
                                          onRemoveChipWithID !== undefined
                                              ? () => onRemoveChipWithID(chip.id)
                                              : undefined
                                      }
                                  />
                              ))
                            : null}
                    </div>
                </div>

                {icon && (
                    <div
                        className={twm(
                            'absolute top-0 bottom-0 left-[19px] z-20 flex items-center justify-center',
                            size === 'small' ? 'w-4' : 'w-5',
                            disabled && 'opacity-40'
                        )}
                    >
                        {typeof icon === 'string' ? (
                            <Icon
                                icon={icon as IconNameAndCategoryString}
                                className="text-gray-700"
                            />
                        ) : (
                            icon
                        )}
                    </div>
                )}

                {(isSecondaryButtonVisible || isClearButtonVisible || isLoading) && (
                    <div
                        className="absolute top-0 right-0 bottom-0 flex items-center justify-center gap-x-2 pl-3"
                        style={{ paddingRight: 16 + (clearButtonOffset ?? 0) }}
                        ref={rightSideButtonsContainerRef}
                    >
                        {secondaryButton !== undefined && (
                            <Button
                                onMouseDown={secondaryButton.onClick}
                                intent="tertiaryLink"
                                className="px-0"
                                style={{ fontSize: 14 }}
                            >
                                {secondaryButton.label}
                            </Button>
                        )}

                        {isLoading && (
                            <div className="z-20 flex items-center justify-center text-gray-600">
                                <Spinner variant="thick" className="size-6" />
                            </div>
                        )}

                        {isClearButtonVisible && (
                            <button
                                ref={clearButtonRef}
                                onMouseDown={handleClear}
                                onBlur={handleClearButtonBlur}
                                className="z-20 flex items-center justify-center self-center before:absolute before:-z-10 before:hidden before:size-7 before:rounded-full before:bg-black/4 before:content-[''] hover:before:block"
                            >
                                <Icon icon={size === 'small' ? 'close.small' : 'close.medium'} />
                            </button>
                        )}
                    </div>
                )}
            </div>
        )
    }
)
TextField.displayName = 'TextField'
export { TextField }

const TextFieldChip = ({ title, onRemove }: { title: string; onRemove?: () => void }) => {
    return (
        <button
            className="pointer-events-auto flex h-8 items-center justify-center gap-x-2 rounded-3xl border-2 border-gray-300 bg-gray-200 px-3 py-1.5"
            onClick={onRemove}
        >
            <Label size="md" className="text-gray-700">
                {title}
            </Label>
            <Icon icon="close.medium" className="text-gray-700" />
        </button>
    )
}
